import axios from 'axios';

const HR_NOTIFICATION_TYPES = new Set(['hr_request', 'hr_request_status']);
const ATTENDANCE_NOTIFICATION_TYPES = new Set(['attendance_checkin', 'attendance_checkout']);

const LEAVE_CATEGORY_KEYS = new Set([
  'leave request',
  'leave resume',
  'time off request',
]);

function backendUrl() {
  return process.env.REACT_APP_BACKEND_URL;
}

export function isActionableNotification(notification) {
  return Boolean(notification?.type && notification?.request_id);
}

export function isHrNotificationType(type) {
  return HR_NOTIFICATION_TYPES.has(type);
}

export function isAttendanceNotificationType(type) {
  return ATTENDANCE_NOTIFICATION_TYPES.has(type);
}

export function isHrLeaveRequest(hrRequest) {
  if (hrRequest?.is_leave_request != null) {
    return Boolean(hrRequest.is_leave_request);
  }
  const category = (hrRequest?.request_category || hrRequest?.category_name || '')
    .toString()
    .replace(/\u00a0/g, ' ')
    .trim()
    .toLowerCase();
  return LEAVE_CATEGORY_KEYS.has(category);
}

function isOwnHrRequest(userProfile, hrRequest) {
  const userIds = [userProfile?.employee_id, userProfile?.employee_no].filter(Boolean);
  const requesterIds = [hrRequest?.employee_id, hrRequest?.employee_no].filter(Boolean);
  return userIds.some((uid) => requesterIds.includes(uid));
}

export function resolveHrRequestView(userProfile, hrRequest, notificationType) {
  const isLeave = isHrLeaveRequest(hrRequest);
  const role = userProfile?.role;
  const own = isOwnHrRequest(userProfile, hrRequest);

  if (role === 'staff') {
    return isLeave ? 'my_leave_requests' : 'my_admin_requests';
  }
  if (role === 'manager') {
    if (notificationType === 'hr_request' && !own) {
      return 'my_team_requests';
    }
    return isLeave ? 'my_leave_requests' : 'my_admin_requests';
  }
  if (role === 'admin' || role === 'super') {
    return isLeave ? 'requests' : 'admin_requests';
  }
  return 'requests';
}

export function provisionalHrView(userProfile) {
  const role = userProfile?.role;
  if (role === 'staff') return 'my_leave_requests';
  if (role === 'manager') return 'my_team_requests';
  return 'requests';
}

export function resolveTargetView(notification, userProfile) {
  const type = notification?.type;
  switch (type) {
    case 'announcement':
      return 'announcements';
    case 'policy_training':
      return 'policies_trainings';
    case 'hr_request':
    case 'hr_request_status':
      return null;
    case 'attendance_checkin':
    case 'attendance_checkout': {
      const role = userProfile?.role;
      if (role === 'admin' || role === 'super' || role === 'manager') {
        return 'allCheckInOut';
      }
      if (role === 'staff') {
        return 'checkInOut';
      }
      return 'employees';
    }
    default:
      return null;
  }
}

export function parseNotificationActionError(error) {
  const status = error?.response?.status;
  if (status === 403) {
    return 'You do not have access to this item.';
  }
  if (status === 404) {
    return 'This item is no longer available.';
  }
  return 'Could not open this notification. Please try again.';
}

export async function fetchAnnouncementById(entityId, headers) {
  const response = await axios.get(
    `${backendUrl()}/api/announcements/${encodeURIComponent(entityId)}`,
    { headers }
  );
  return response.data;
}

export async function fetchPolicyTrainingById(entityId, headers) {
  const response = await axios.get(
    `${backendUrl()}/api/policies-trainings/${encodeURIComponent(entityId)}`,
    { headers }
  );
  return response.data;
}

export async function fetchHrRequestById(entityId, headers) {
  const response = await axios.get(
    `${backendUrl()}/api/hr-requests/${encodeURIComponent(entityId)}`,
    { headers }
  );
  return response.data;
}

export async function fetchAttendanceById(entityId, headers) {
  const response = await axios.get(
    `${backendUrl()}/api/attendance/${encodeURIComponent(entityId)}`,
    { headers }
  );
  return response.data;
}

export async function fetchEmployeeById(entityId, headers) {
  const response = await axios.get(
    `${backendUrl()}/api/employees/${encodeURIComponent(entityId)}`,
    { headers }
  );
  return response.data;
}
